/* TreeViewRenderer.js */

/* eslint-disable no-unused-vars */
"use strict";
const _ = require("lodash");
const classNames = require("classnames");
const escapeHTML = require("escape-html");
const tag = require("html5-tag");
const InfiniteTree = require("infinite-tree");

class TreeViewRenderer {
    constructor(model) {
        this._model = model;
        this._moduleId = Date.now().toString();
        this._rootElem = document.createElement(`tree${this._moduleId}`);
        this._rootElem.classList.add("tree");
        this._rootElem.component = this._model;

        this._renderer = undefined;
        this._currentNode = undefined;
        this._simpleRenderer = this._model._simpleTree;
        this._treeActions = this._model._treeActions;
        this._defaultSelection = this._model._defaultSelection;
        this._selectedNodes = [];

        this._readonly = model.readonly;
        this.treeRenderer = (node, treeOptions) => {
            const {
                id,
                name,
                type,
                /*fileType,*/
                fileSize,
                imageType,
                loadOnDemand = false,
                children,
                state,
                props = {}
            } = node;
            const droppable = treeOptions.droppable && props.droppable;
            const {
                depth,
                open,
                path,
                total,
                selected = false,
                filtered,
                checked,
                indeterminate
            } = state;
            const childrenLength = Object.keys(children).length;
            const more = node.hasChildren();

            if (filtered === false) {
                return;
            }

            let togglerContent = tag(
                "i",
                {
                    class: classNames("folder")
                },
                ""
            );

            if (!more && loadOnDemand && type !== "image") {
                togglerContent = tag(
                    "i",
                    {
                        class: classNames("", "folder")
                    },
                    ""
                );
            }
            if (more && open && type !== "image") {
                togglerContent = tag(
                    "i",
                    {
                        class: classNames("open", "folder")
                    },
                    ""
                );
            }
            if (more && !open && type !== "image") {
                togglerContent = tag(
                    "i",
                    {
                        class: classNames("", "folder")
                    },
                    ""
                );
            }
            if (type === "image") {
                togglerContent = tag(
                    "i",
                    {
                        class: classNames("", `file ${imageType ? imageType.toLowerCase() : ""}`),
                        title: `${imageType ? imageType : ""}`
                    },
                    ""
                );
            }
            if (state.expanding && !state.loading) {
                togglerContent = tag(
                    "i",
                    {
                        class: classNames("glyphicon", "glyphicon-refresh", "rotating")
                    },
                    "_"
                );
            }
            if (state.collapsing) {
                // TODO
            }
            const toggler = tag(
                "a",
                {
                    class: (() => {
                        if (!more && loadOnDemand) {
                            return classNames(treeOptions.togglerClass, "infinite-tree-closed");
                        }
                        if (more && open) {
                            return classNames(treeOptions.togglerClass);
                        }
                        if (more && !open) {
                            return classNames(treeOptions.togglerClass, "infinite-tree-closed");
                        }
                        return classNames(treeOptions.togglerClass);
                    })()
                },
                togglerContent
            );

            const icon = tag(
                "i",
                {
                    class: classNames(
                        "infinite-tree-folder-icon",
                        "glyphicon",
                        { "glyphicon-folder-open": more && open },
                        { "glyphicon-folder-close": more && !open },
                        { "glyphicon-file": !more }
                    )
                },
                ""
            );

            const checkbox = tag("input", {
                type: "checkbox",
                style: "display: inline-block; margin: 0 4px",
                class: "checkbox",
                checked: checked,
                "data-checked": checked,
                "data-indeterminate": indeterminate
            });

            const title = tag(
                "span",
                {
                    class: classNames("infinite-tree-title")
                },
                escapeHTML(loadOnDemand ? "(loadOnDemand) " + name : name)
            );

            const fileDetails = tag(
                "span",
                { class: classNames("fileDetails") },
                `${fileSize} bytes`
            );

            const loadingIcon = tag(
                "i",
                {
                    style: "margin-left: 5px",
                    class: classNames(
                        { hidden: !state.loading },
                        "glyphicon",
                        "glyphicon-refresh",
                        { rotating: state.loading }
                    )
                },
                ""
            );

            const arrow = tag(
                "a",
                {
                    class: classNames("arrow infinite-tree-toggler", { open: open }, { more: more })
                },
                ""
            );

            const deleteIcon =
                this._treeActions.find("delete") && !this._readonly
                    ? tag("button", { class: "deleteIcon" })
                    : "";

            const treeNode = tag(
                "div",
                {
                    class: "infinite-tree-node",
                    style: "margin-left: " + depth * 18 + "px"
                },
                arrow +
                    toggler +
                    (!this._simpleRenderer ? checkbox : "") +
                    icon +
                    title +
                    loadingIcon +
                    deleteIcon +
                    (type === "image" ? fileDetails : "")
            );

            let treeNodeAttributes = {
                draggable: (!this._readonly).toString(),
                "data-id": id,
                "data-expanded": more && open,
                "data-depth": depth,
                "data-path": path,
                "data-selected": selected,
                "data-children": childrenLength,
                "data-total": total,
                class: classNames("infinite-tree-item", {
                    "infinite-tree-selected": selected
                })
            };
            if (droppable) {
                treeNodeAttributes.droppable = true;
            }

            return tag("div", treeNodeAttributes, treeNode);
        };

        this.tree = undefined;

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._model.on("updateNode", (this._updateNode = this._updateNode.bind(this)));
        this._model.on("ReadOnly", (this._onReadonlyChange = this._onReadonlyChange.bind(this)));
    }
    _unregisterListeners() {
        this._model.removeListener("updateNode", this._updateNode);
        this._model.removeListener("ReadOnly", this._onReadonlyChange);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    shutdown() {
        return new Promise((resolve) => {
            this._unregisterListeners();
            resolve();
        });
    }
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return this._buildTree();
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }

    _buildTree() {
        return new Promise((resolve /*,reject*/) => {
            this.tree = new InfiniteTree(this._rootElem, {
                autoOpen: this._model._autoOpen, // Defaults to false
                droppable: {
                    hoverClass: "infinite-tree-drop-hover",
                    accept: () => {
                        // dropdown logic goes here
                        return true;
                    },
                    drop: (e, opts) => {
                        const { draggableTarget, droppableTarget, node } = opts;
                        if (droppableTarget.dataset.id) {
                            this.model._dragDroped(draggableTarget, droppableTarget, node);
                        }
                    }
                },
                loadNodes: (parentNode, next) => {
                    // Loading
                    const nodes = [];
                    nodes.length = 1000;
                    for (let i = 0; i < nodes.length; ++i) {
                        nodes[i] = {
                            id: `${parentNode.id}.${i}`,
                            name: `${parentNode.name}.${i}`,
                            loadOnDemand: true
                        };
                    }

                    next(null, nodes, () => {
                        // Completed
                    });
                },
                rowRenderer: this.treeRenderer,
                selectable: this._model._selectable, // Defaults to true
                shouldSelectNode: (node) => {
                    // Defaults to null
                    if (!node || node === this.tree.getSelectedNode()) {
                        return false; // Prevent from deselecting the current node
                    }
                    return true;
                }
            });

            const updatePreview = (node) => {
                if (node) {
                    const o = {
                        id: node.id,
                        name: node.name,
                        children: node.children ? node.children.length : 0,
                        parent: node.state.depth === 0 ? "(root)" : node.parent.id,
                        state: {
                            depth: node.state.depth,
                            open: node.state.open,
                            path: node.state.path,
                            prefixMask: node.state.prefixMask,
                            selected: node.state.selected,
                            total: node.state.total,
                            checked: node.state.checked,
                            indeterminate: node.state.indeterminate,
                            filtered: node.state.filtered,
                            unfilteredChildren: node.state.unfilteredChildren
                                ? node.state.unfilteredChildren.map((node) => node.id)
                                : undefined,
                            unfilteredTotal: node.state.unfilteredTotal
                        }
                    };
                    if (node.loadOnDemand !== undefined) {
                        o.loadOnDemand = node.loadOnDemand;
                    }
                } else {
                    this._model._triggerNothingSelected();
                }
            };

            let selectedNodes = this._selectedNodes;
            this.tree.on("doubleClick", (event) => {
                const currentNode = this.tree.getNodeFromPoint(event.clientX, event.clientY);
                this._currentNode = this.tree.getNodeFromPoint(event.clientX, event.clientY);
                if (event.target.className === "infinite-tree-title" && !this._readonly) {
                    this.model._triggerUserSelectedPathForEdit(this._currentNode);
                }
            });
            this.tree.on("click", (event) => {
                const currentNode = this.tree.getNodeFromPoint(event.clientX, event.clientY);
                if (!currentNode) {
                    return;
                }

                if (event.target.className === "checkbox") {
                    event.stopPropagation();
                    this.tree.checkNode(currentNode);
                    return;
                }

                if (event.target.className === "deleteIcon") {
                    event.stopPropagation();
                    this.model._triggerDelete(currentNode);
                    return;
                }
                this._currentNode = currentNode;

                const multipleSelectionMode = event.ctrlKey || event.metaKey;

                if (!multipleSelectionMode) {
                    if (selectedNodes.length > 0) {
                        // Call event.stopPropagation() to stop event bubbling
                        event.stopPropagation();

                        // Empty an array of selected nodes
                        selectedNodes.forEach((selectedNode) => {
                            selectedNode.state.selected = false;
                            this.tree.updateNode(selectedNode, {}, { shallowRendering: true });
                        });
                        selectedNodes = [];

                        // Select current node
                        this.tree.selectNode(currentNode);
                    }
                    return;
                }

                // Call event.stopPropagation() to stop event bubbling
                event.stopPropagation();

                const selectedNode = this.tree.getSelectedNode();
                if (selectedNodes.length === 0 && selectedNode) {
                    selectedNodes.push(selectedNode);
                    this.tree.state.selectedNode = null;
                }

                const index = selectedNodes.indexOf(currentNode);

                // Remove current node if the array length of selected nodes is greater than 1
                if (index >= 0 && selectedNodes.length > 0) {
                    currentNode.state.selected = false;
                    selectedNodes.splice(index, 1);
                    this.tree.updateNode(currentNode, {}, { shallowRendering: true });
                }

                // Add current node to the selected nodes
                if (index < 0) {
                    currentNode.state.selected = true;
                    selectedNodes.push(currentNode);
                    this.tree.updateNode(currentNode, {}, { shallowRendering: true });
                }
                this._model._triggerUserSelectedPaths(selectedNodes);
            });
            this.tree.on("keyDown", (event) => {
                event.preventDefault();

                //console.log('keyDown', event);
                const node = this.tree.getSelectedNode();
                const nodeIndex = this.tree.getSelectedIndex();

                if (event.keyCode === 37) {
                    // Left
                    this.tree.closeNode(node);
                } else if (event.keyCode === 38) {
                    // Up
                    if (this.tree.filtered) {
                        let prevNode = node;
                        for (let i = nodeIndex - 1; i >= 0; --i) {
                            if (this.tree.nodes[i].state.filtered) {
                                prevNode = this.tree.nodes[i];
                                break;
                            }
                        }
                        this.tree.selectNode(prevNode);
                    } else {
                        const prevNode = this.tree.nodes[nodeIndex - 1] || node;
                        this.tree.selectNode(prevNode);
                    }
                } else if (event.keyCode === 39) {
                    // Right
                    this.tree.openNode(node);
                } else if (event.keyCode === 40) {
                    // Down
                    if (this.tree.filtered) {
                        let nextNode = node;
                        for (let i = nodeIndex + 1; i < this.tree.nodes.length; ++i) {
                            if (this.tree.nodes[i].state.filtered) {
                                nextNode = this.tree.nodes[i];
                                break;
                            }
                        }
                        this.tree.selectNode(nextNode);
                    } else {
                        const nextNode = this.tree.nodes[nodeIndex + 1] || node;
                        this.tree.selectNode(nextNode);
                    }
                }
            });
            this.tree.on("keyUp", () => {
                //console.log('keyUp', event);
            });
            this.tree.on("contentWillUpdate", () => {
                //console.log('contentWillUpdate');
            });
            this.tree.on("contentDidUpdate", () => {
                //console.log('contentDidUpdate');
                // updateCheckboxState(this.tree);

                const node = this.tree.getSelectedNode();
                updatePreview(node);
            });
            this.tree.on("openNode", (node) => {
                this._model._triggerOpenNode(node);
            });
            this.tree.on("closeNode", () => {
                //console.log('closeNode', node);
            });
            this.tree.on("selectNode", (node) => {
                //console.log('selectNode', node);
                this._currentNode = node;
                this.model._triggerUserSelectedPath(this._currentNode);
            });
            this.tree.on("willOpenNode", () => {
                //console.log('willOpenNode:', node);
            });
            this.tree.on("willCloseNode", () => {
                //console.log('willCloseNode:', node);
            });
            this.tree.on("willSelectNode", () => {
                //console.log('willSelectNode:', node);
            });
            this.tree.on("clusterDidChange", () => {
                //console.log('clusterDidChange');

                // updateCheckboxState(this.tree);

                // No overlay on filtered mode

                if (this.tree.filtered) {
                    return;
                }
            });

            this.tree.loadData(this._model._data);
            for (let i = 0; i < this.tree.nodes.length; ++i) {
                if (this.tree.nodes[i].id === this._defaultSelection) {
                    this.tree.nodes[i].selected = true;
                    this.tree.selectNode(this.tree.nodes[i]);
                    break;
                }
            }

            resolve();
        });
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/

    async _updateNode(data = []) {
        if (data.length > 0) {
            data.forEach((item) => {
                let parentId = item.parentId;
                for (let i = 0; i < this.tree.nodes.length; ++i) {
                    let currentNode = this.tree.nodes[i];
                    if (currentNode.id === parentId) {
                        if (_.find(currentNode.children, ["id", item.id]) === undefined) {
                            /* 
                        Add the current node as parent to item if not already present
                        parentId and currentNode id already match
                        */
                            if (!(item.parent && item.parent.id)) {
                                item.parent = currentNode;
                            }
                            this.tree.appendChildNode(item, currentNode);
                            /* Not opening if id is null for Node */
                            const nodeToBeOpened =
                                currentNode.children.length === 1
                                    ? currentNode.parent.id
                                        ? currentNode.parent
                                        : null
                                    : currentNode;
                            if (nodeToBeOpened) {
                                this.tree.openNode(nodeToBeOpened);
                            }
                            this.tree.openNode(currentNode);
                            return;
                        }
                    }
                }
            });
        }
    }

    _updateTree(data) {
        if (this.tree) {
            this.tree.loadData(data);
        }
    }
    async _removeNode(data = {}) {
        if (data !== {}) {
            if (!this.tree.state.selectedNode) {
                this.tree.selectNode(data);
            }
            if (this.tree.removeNode(data)) {
                const nodeIdx = this._selectedNodes.indexOf(data);
                if (nodeIdx >= 0) {
                    this._selectedNodes.splice(nodeIdx, 1);
                }
            }
            return;
        }
    }
    _selectSelection() {
        this._currentNode.state.selected = true;
        this.tree.updateNode(this._currentNode, {}, { shallowRendering: true });
    }
    _resetSelection() {
        this._currentNode.state.selected = false;
        this.tree.updateNode(this._currentNode, {}, { shallowRendering: true });
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    _onReadonlyChange(state) {
        this._readonly = state;
        this._updateTree(this._model.data);
    }
}

module.exports = TreeViewRenderer;
